import {Link, useLocation} from '@github-ui/react-router'
import {NavList, PageLayout} from '@primer/react'
import {useEffect} from 'react'

import {FooterLinks} from '../components/FooterLinks'
import styles from './hash-nav.module.css'

const sections = [
  {id: 'overview', title: 'Overview'},
  {id: 'getting-started', title: 'Getting Started'},
  {id: 'configuration', title: 'Configuration'},
  {id: 'advanced-usage', title: 'Advanced Usage'},
  {id: 'troubleshooting', title: 'Troubleshooting'},
] as const

// Global counter to track renders across navigations
let globalRenderCount = 0

/**
 * HashNav demonstrates hash-based navigation within a single page.
 *
 * This component is useful for testing/reproducing the bug where hash navigation
 * causes a full page re-render in the DataRouter implementation.
 *
 * Expected behavior: Clicking hash links should smoothly scroll to the section
 * without causing a full page re-render.
 *
 * Bug behavior: The entire page re-renders when navigating via hash links.
 */
export function HashNav() {
  const location = useLocation()
  const currentHash = location.hash.replace('#', '')

  // Increment global render count on each render
  globalRenderCount++
  const renderCount = globalRenderCount

  // Scroll to the hash target on mount and hash change
  useEffect(() => {
    if (location.hash) {
      const element = document.getElementById(location.hash.replace('#', ''))
      if (element) {
        element.scrollIntoView({behavior: 'smooth'})
      }
    }
  }, [location.hash])

  return (
    <>
      <h2 data-hpc>Hash Navigation Example</h2>

      <div className={styles.bugNotice}>
        <p className={styles.bugNoticeTitle}>🐛 Bug Reproduction: Hash Navigation Causes Page Re-render</p>
        <p className={styles.bugNoticeText}>
          This page demonstrates a bug where clicking hash links causes the entire page to re-render instead of just
          scrolling to the target section.
        </p>
        <p className={styles.bugNoticeText}>
          <strong>Render count: {renderCount}</strong>
          <br />
          <span className={styles.muted}>
            (This should not increase when clicking hash links, but it does due to the bug)
          </span>
        </p>
        <p className={styles.bugNoticeText}>
          <strong>Current hash:</strong> <span>{location.hash || '(none)'}</span>
        </p>
      </div>

      <PageLayout>
        <PageLayout.Pane position="start" sticky>
          <NavList aria-label="Page sections">
            <NavList.Group title="On this page">
              {sections.map(section => (
                <NavList.Item
                  key={section.id}
                  as={Link}
                  to={`#${section.id}`}
                  aria-current={currentHash === section.id ? 'location' : undefined}
                >
                  {section.title}
                </NavList.Item>
              ))}
            </NavList.Group>
          </NavList>
        </PageLayout.Pane>

        <PageLayout.Content>
          {sections.map(section => (
            <Section key={section.id} id={section.id} title={section.title} />
          ))}
        </PageLayout.Content>
      </PageLayout>

      <FooterLinks />
    </>
  )
}

function Section({id, title}: {id: string; title: string}) {
  return (
    <section id={id} className={styles.section}>
      <h3 className={styles.sectionHeading}>
        <Link to={`#${id}`} className={styles.headingLink}>
          {title}
        </Link>
      </h3>
      <p className={styles.sectionText}>
        This is the content for the <strong>{title}</strong> section. Lorem ipsum dolor sit amet, consectetur adipiscing
        elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud
        exercitation ullamco laboris.
      </p>
      <p className={styles.sectionText}>
        Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur
        sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum.
      </p>
      <p className={styles.sectionText}>
        Sed ut perspiciatis unde omnis iste natus error sit voluptatem accusantium doloremque laudantium, totam rem
        aperiam, eaque ipsa quae ab illo inventore veritatis et quasi architecto beatae vitae dicta sunt explicabo.
      </p>
    </section>
  )
}

try{ HashNav.displayName ||= 'HashNav' } catch {}
try{ Section.displayName ||= 'Section' } catch {}